import { usePost } from "@albertine/api-client";
import { ServiceLanguage, ServiceLanguageMap } from "@albertine/i18n";
import type { TestTranslationRequest } from "@albertine/types/openapi/lmt";
import { useState } from "react";
import { Field, FieldLabel } from "@/components/ui/field";
import { Textarea } from "@/components/ui/textarea";
import Button from "../components/Common/Buttons";
import Dropdown, { type DropdownOption } from "../components/Common/Dropdown";
import Label from "../components/Common/Label";
import LoadingSpinner from "../components/LoadingSpinner/LoadingSpinner";

const LANGUAGE_OPTIONS: DropdownOption[] = ServiceLanguage.options.map((language) => ({
    value: language,
    label: ServiceLanguageMap[language],
}));

const SOURCE_LANGUAGE_OPTIONS: DropdownOption[] = [
    { value: null, label: "Detect" },
    ...LANGUAGE_OPTIONS,
];

const TRANSLATOR_OPTIONS: DropdownOption[] = [
    { value: "chat-llm", label: "Chat LLM" },
    { value: "chat-nmt", label: "Chat NMT (Google Translate)" },
];

export function TestTranslation() {
    const [text, setText] = useState("");
    const [sourceLanguage, setSourceLanguage] = useState<string | null>(null);
    const [targetLanguage, setTargetLanguage] = useState<string>("de");
    const [translator, setTranslator] = useState<string>("chat-llm");
    const { mutate, isPending, data, error } = usePost("testTranslation");

    const handleTranslate = () => {
        const parsedTarget = ServiceLanguage.safeParse(targetLanguage);
        const parsedTranslator = parseTranslator(translator);
        const trimmedText = text.trim();
        if (!parsedTarget.success || parsedTranslator === undefined || !trimmedText) {
            return;
        }

        const parsedSource = ServiceLanguage.safeParse(sourceLanguage);
        mutate({
            json: {
                text: trimmedText,
                targetLanguage: parsedTarget.data,
                translator: parsedTranslator,
                ...(parsedSource.success ? { sourceLanguage: parsedSource.data } : {}),
            },
        });
    };

    return (
        <>
            <Field>
                <FieldLabel htmlFor="test-translation-text">Text</FieldLabel>
                <Textarea
                    id="test-translation-text"
                    value={text}
                    onChange={(event) => setText(event.target.value)}
                />
            </Field>
            <Dropdown
                label="Source language"
                optionList={SOURCE_LANGUAGE_OPTIONS}
                currentValue={sourceLanguage}
                onChange={setSourceLanguage}
            />
            <Dropdown
                label="Target language"
                optionList={LANGUAGE_OPTIONS}
                currentValue={targetLanguage}
                onChange={(value) => {
                    if (value) setTargetLanguage(value);
                }}
            />
            <Dropdown
                label="Translator"
                optionList={TRANSLATOR_OPTIONS}
                currentValue={translator}
                onChange={(value) => {
                    if (value) setTranslator(value);
                }}
            />
            <Button.Secondary
                label="Translate"
                showLoadingSpinner={isPending}
                disabled={!text.trim() || isPending}
                onClick={handleTranslate}
            />

            <Label.Large.N10>Result</Label.Large.N10>
            {isPending && <LoadingSpinner />}
            {!isPending && data && (
                <>
                    <p>Duration: {data.durationMs} ms</p>
                    <p>
                        Detected language:{" "}
                        {formatDetectedLanguage(data.detectedLanguage)}
                    </p>
                    <pre>{data.text}</pre>
                </>
            )}
            {!isPending && error && <pre>{JSON.stringify(error, undefined, 2)}</pre>}
        </>
    );
}

function parseTranslator(
    value: string,
): TestTranslationRequest["translator"] | undefined {
    if (value === "chat-llm" || value === "chat-nmt") {
        return value;
    }
    return undefined;
}

function formatDetectedLanguage(detectedLanguage: string | null): string {
    if (detectedLanguage === null || detectedLanguage.length === 0) {
        return "unknown";
    }
    const parsed = ServiceLanguage.safeParse(detectedLanguage);
    return parsed.success ? ServiceLanguageMap[parsed.data] : detectedLanguage;
}
